import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import basketball from '../icon/icons/basketball-ball.svg';
import soccer from '../icon/icons/soccer-ball.svg';
import voleyball from '../icon/icons/voleyball-ball.svg';

const loaderAnimation = keyframes`
  from {
    background-image: url(${soccer});
    transform: translateY(0);
  }
  12.5%{
    background-image: url(${soccer});
    transform: translateY(-4px);
  }
  25%{
    background-image: url(${soccer});
    transform: translateY(0);
  }
  37.5% {
    background-image: url(${voleyball});
    transform: translateY(0);
  }
  50%{
    background-image: url(${voleyball});
    transform: translateY(-4px);
  }
  62.5%{
    background-image: url(${voleyball});
    transform: translateY(0);
  }
  75% {
    background-image: url(${basketball});
    transform: translateY(0);
  }
  87.5%{
    background-image: url(${basketball});
    transform: translateY(-4px);
  }
  to{
    background-image: url(${basketball});
    transform: translateY(0);
  }
`;

export const Button = styled.button`
  padding: 8px 16px;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.radii.card};
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
  user-select: none;
  border-color: transparent;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: normal !important;

  transition: all 0.2s ease-in-out;

  // Фокус — штатное кольцо браузера, у всех вариантов одно. Оно появляется только с клавиатуры
  // (:focus-visible) и двухцветное, поэтому видно и на светлой, и на тёмной подложке. Своя рамка
  // фокуса у варианта рисовалась бы вторым кольцом рядом с браузерным (IMGM-1495).

  //Loading

  &.loading {
    position: relative;
    user-select: none;
    pointer-events: none;
    color: transparent !important;

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      background-color: inherit;
      border-radius: inherit;
    }

    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      content: '';
      display: block;
      width: 14px;
      height: 14px;
      margin-top: -7px;
      margin-left: -7px;
      background-size: 14px 14px;
      animation: ${loaderAnimation} 3s ease infinite alternate;
    }
  }

  &.stretch {
    width: 100%;
  }

  &.slim {
    padding: 4px 8px;
    border-radius: ${({ theme }) => theme.radii.control};
  }

  &[disabled] {
    cursor: default;
    pointer-events: none;

    &:hover {
      cursor: not-allowed;
    }
  }

  //default status

  &:hover {
    background-color: ${({ theme }) => theme.colors.LightGold};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.Gold};
    color: ${({ theme }) => theme.colors.InkOnDark};
  }

  &[disabled] {
    color: ${({ theme }) => theme.colors.WhiteGray60};
  }

  // primary

  &.primary {
    background-color: ${({ theme }) => theme.colors.DarkYellow};
    color: ${({ theme }) => theme.colors.InkPrimary};

    &[disabled] {
      background: ${({ theme }) => theme.colors.DarkYellowAlpha40};
    }

    //default status

    &:hover {
      background-color: ${({ theme }) => theme.colors.LightGold};
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.Gold};
      color: ${({ theme }) => theme.colors.InkOnDark};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.WhiteGray60};
    }
  }

  &.secondary {
    border-color: ${({ theme }) => theme.colors.DarkYellow};
    color: ${({ theme }) => theme.colors.InkPrimary};

    &.loading {
      background-color: ${({ theme }) => theme.colors.SurfaceCard};
    }

    &[disabled] {
      border-color: ${({ theme }) => theme.colors.DarkYellowAlpha40};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.LightGold};
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.Gold};
      color: ${({ theme }) => theme.colors.InkOnDark};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.WhiteGray60};
    }
  }

  &.dark {
    background-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
    border-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
    color: ${({ theme }) => theme.colors.InkOnDark};

    &:hover {
      color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &:active {
      border-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
      color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }

    &.loading {
      &::after {
        filter: invert(1) saturate(5) hue-rotate(351deg);
      }
    }
  }

  &.light {
    background-color: ${({ theme }) => theme.colors.SurfaceCard};
    border-color: ${({ theme }) => theme.colors.SurfaceCard};
    color: ${({ theme }) => theme.colors.SurfaceRaisinBlack50};

    &:hover {
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      border-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
      border-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &[disabled] {
      border-color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
  }

  &.tertiary {
    background-color: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.colors.AccentNaplesYellow};

    &:hover {
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      border-color: ${({ theme }) => theme.colors.AccentCornsilk};
      color: ${({ theme }) => theme.colors.AccentJonquil};
    }

    &.for-dark:hover {
      background-color: transparent;
      border-color: transparent;
      color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &:active {
      border-color: ${({ theme }) => theme.colors.AccentCornsilk};
      color: ${({ theme }) => theme.colors.AccentJonquil};
      background:
        radial-gradient(
          circle at center,
          ${({ theme }) => theme.colors.AccentCornsilk} 9%,
          ${({ theme }) => theme.colors.AccentJonquil}00 10%
        ),
        transparent;
    }

    &.for-dark:active {
      background: ${({ theme }) => theme.colors.AccentMediumChampagne};
      border-color: transparent;
      color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
  }

  &.green {
    background-color: ${({ theme }) => theme.colors.Green};
    border-color: ${({ theme }) => theme.colors.Green};

    &:hover {
      opacity: 0.85;
    }

    &:active {
      opacity: 0.7;
    }

    &[disabled] {
      background-color: ${({ theme }) => theme.colors.Green};
      border-color: ${({ theme }) => theme.colors.Green};
      color: ${({ theme }) => theme.colors.WhiteGray60};
      opacity: 0.4;
    }

    &.loading {
      &::after {
        filter: invert(1) saturate(5) hue-rotate(351deg);
      }
    }
  }

  // Необратимое действие. Текст на красной заливке — тёмный: белый на CaseError даёт 2.71:1 и не проходит AA.
  &.danger {
    background-color: ${({ theme }) => theme.colors.CaseError};
    border-color: ${({ theme }) => theme.colors.CaseError};
    color: ${({ theme }) => theme.colors.InkPrimary};

    // Ховер и нажатие — тот же красный, слегка светлее и темнее (слой состояния, как в Material 3):
    // смена оттенка на тёмно-коричневый CaseErrorText выглядела грязно.
    &:hover {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.CaseError} 85%, ${({ theme }) => theme.colors.InkOnDark});
      border-color: color-mix(in srgb, ${({ theme }) => theme.colors.CaseError} 85%, ${({ theme }) => theme.colors.InkOnDark});
    }

    &:active {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.CaseError} 88%, ${({ theme }) => theme.colors.InkPrimary});
      border-color: color-mix(in srgb, ${({ theme }) => theme.colors.CaseError} 88%, ${({ theme }) => theme.colors.InkPrimary});
    }

    &[disabled] {
      background-color: ${({ theme }) => theme.colors.CaseErrorSurfaceActive};
      border-color: ${({ theme }) => theme.colors.CaseErrorSurfaceActive};
      color: ${({ theme }) => theme.colors.InkDisabled};
    }
  }

  &.gray {
    background-color: ${({ theme }) => theme.colors.WhiteGray};
    border-color: ${({ theme }) => theme.colors.WhiteGray};
    color: ${({ theme }) => theme.colors.InkOnDark};

    &:hover {
      color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &:active {
      border-color: ${({ theme }) => theme.colors.AccentJonquil};
      color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }

    &.loading {
      &::after {
        filter: invert(1) saturate(5) hue-rotate(351deg);
      }
    }
  }
`;
